/**
 * 服务商品新版配置（设计稿 科创服务-服务商品_slices）
 * 图片统一走 service_product_new/aliases 短英文名
 *
 * 切图映射（aliases ← 科创服务-服务商品_slices）:
 * banner-bg ← beijing@2x
 * title-mark ← 服务商品@2x
 * icon-type ← 服务类型@2x
 * icon-star ← 多边形 1@2x
 * icon-unit ← w_单位-209@2x
 * cover-fallback ← 图层 5@2x
 * cover-fallback-alt ← 图层 10@2x
 */
import { deepCloneConfig } from '@/utils/deepCloneConfig'
import { applyPortalZhToRu } from '@/utils/applyPortalZhToRu'
import { getCurrentLocale } from '@/utils/i18nText'
import { L, resolveLocaleImages } from '@/utils/localeAsset'

const A = (name) => L(`service_product_new/aliases/${name}`)
const Nav = (name) => L(`home_new/aliases/${name}`)

const zhCN = {
  siteInfo: {
    title: '杨凌农业科技成果转化综合信息服务平台',
    logo: L('shouye/web-logo.png'),
    loginText: '登录',
    registerText: '注册',
    logoutText: '退出'
  },
  breadcrumb: {
    home: '首页',
    parent: '科创服务',
    parentPath: '/innovation-service',
    current: '服务商品'
  },
  header: {
    titleMain1: '服务',
    titleMain2: '商品',
    titleSub: '优质科技服务，助力创新发展',
    searchPlaceholder: '请输入服务商品名称或关键词搜索',
    searchButton: '搜索',
    bannerBg: A('banner-bg.png')
  },
  modules: [
    { id: 'home', name: '首页', enName: 'HOME', icon: Nav('nav-home.png'), link: '/index' },
    { id: 'innovation', name: '走进杨凌', enName: 'YANGLING', icon: Nav('nav-innovation.png'), link: '/innovation' },
    { id: 'supply', name: '供需中心', enName: 'SUPPLY & DEMAND', icon: Nav('nav-supply.png'), link: '/supply-demand' },
    { id: 'evaluation', name: '价值评估', enName: 'EVALUATE', icon: Nav('nav-evaluation.png'), link: '/value-assessment' },
    { id: 'manager', name: '技术经理人', enName: 'MANAGER', icon: Nav('nav-manager.png'), link: '/manager' },
    { id: 'transaction', name: '交易中心', enName: 'TRANSACTION', icon: Nav('nav-transaction.png'), link: '/transaction' },
    { id: 'service', name: '科创服务', enName: 'SERVICE', icon: Nav('nav-service.png'), link: '/innovation-service' },
    { id: 'talent', name: '科技人才', enName: 'TALENT', icon: Nav('nav-talent.png'), link: '/talent' },
    { id: 'enterprise', name: '农业企业', enName: 'ENTERPRISE', icon: Nav('nav-enterprise.png'), link: '/agriculture-enterprise' }
  ],
  icons: {
    type: A('icon-type.png'),
    star: A('icon-star.png'),
    unit: A('icon-unit.png')
  },
  typeFilter: {
    label: '类型',
    allLabel: '全部'
  },
  card: {
    providerLabel: '服务机构：',
    emptyTip: '待接口提供',
    coverFallback: L('default.png'),
    starIcon: A('icon-star.png'),
    unitIcon: A('icon-unit.png')
  },
  pagination: {
    pageInfo: '共{total}条，每页显示{pageSize}条',
    prevPage: '上一页',
    nextPage: '下一页',
    loading: '加载中...'
  },
  footer: {
    copyright: '© 2026 杨凌农业科技成果转化综合信息服务平台 版权所有',
    support: '技术支持：浪潮软件'
  }
}

const enUS = {
  siteInfo: {
    title: 'Yangling Agricultural Science & Technology Achievement Transformation Platform',
    logo: L('shouye/web-logo.png'),
    loginText: 'Login',
    registerText: 'Register',
    logoutText: 'Logout'
  },
  breadcrumb: {
    home: 'Home',
    parent: 'Innovation Service',
    parentPath: '/innovation-service',
    current: 'Service Products'
  },
  header: {
    titleMain1: 'Service',
    titleMain2: 'Products',
    titleSub: 'Quality tech services empowering innovation',
    searchPlaceholder: 'Search by product name or keyword',
    searchButton: 'Search',
    bannerBg: A('banner-bg.png')
  },
  modules: [
    { id: 'home', name: 'Home', enName: 'HOME', icon: Nav('nav-home.png'), link: '/index' },
    { id: 'innovation', name: 'Yangling', enName: 'YANGLING', icon: Nav('nav-innovation.png'), link: '/innovation' },
    { id: 'supply', name: 'Supply & Demand', enName: 'SUPPLY & DEMAND', icon: Nav('nav-supply.png'), link: '/supply-demand' },
    { id: 'evaluation', name: 'Evaluation', enName: 'EVALUATE', icon: Nav('nav-evaluation.png'), link: '/value-assessment' },
    { id: 'manager', name: 'Managers', enName: 'MANAGER', icon: Nav('nav-manager.png'), link: '/manager' },
    { id: 'transaction', name: 'Transaction', enName: 'TRANSACTION', icon: Nav('nav-transaction.png'), link: '/transaction' },
    { id: 'service', name: 'Service', enName: 'SERVICE', icon: Nav('nav-service.png'), link: '/innovation-service' },
    { id: 'talent', name: 'Talent', enName: 'TALENT', icon: Nav('nav-talent.png'), link: '/talent' },
    { id: 'enterprise', name: 'Enterprises', enName: 'ENTERPRISE', icon: Nav('nav-enterprise.png'), link: '/agriculture-enterprise' }
  ],
  icons: zhCN.icons,
  typeFilter: {
    label: 'Type',
    allLabel: 'All'
  },
  card: {
    providerLabel: 'Provider: ',
    emptyTip: '待接口提供',
    coverFallback: L('default.png'),
    starIcon: A('icon-star.png'),
    unitIcon: A('icon-unit.png')
  },
  pagination: {
    pageInfo: '{total} items, {pageSize} per page',
    prevPage: 'Prev',
    nextPage: 'Next',
    loading: 'Loading...'
  },
  footer: {
    copyright: '© 2026 Yangling Agricultural Science & Technology Marketplace. All rights reserved.',
    support: 'Technical support: Inspur Software'
  }
}

export function getServiceProductNewConfig(locale = getCurrentLocale()) {
  let raw
  if (locale === 'en-US') raw = deepCloneConfig(enUS)
  else if (locale === 'ru-RU') raw = applyPortalZhToRu(deepCloneConfig(zhCN))
  else raw = deepCloneConfig(zhCN)
  return resolveLocaleImages(raw, locale)
}
